/**
 * Draws Brick Breaker's field on a canvas, seen from above like the Brick Drop board: a baseplate
 * grid, studded plates for bricks and the paddle, a white ball, golden glints, the aim guide,
 * pops and chips (look from the design's Field part). Units in: studs; `cell` = px per stud.
 */
import type { Brick } from "./levels";
import type { Scene } from "./scene";
import { POP_LIFE } from "./scene";
import { BALL_R, COLS, GOLD, PADDLE_ROW, PADDLE_W, ROWS } from "./world";

const FIELD_BG = "#e9e3d6";
const INK = "#1d1b18";
const PADDLE_COLOR = "#1d1b18";
const CRACK = "#b3aa9b";
const GLINT_PERIOD = 2.4; // s

export interface FieldFrame {
  bricks: Brick[];
  wallVersion: number;
  ball: { x: number; y: number };
  paddle: number;
  serving: boolean;
  /** While serving: launch direction, radians off vertical. */
  aim: number;
  /** Recent ball positions, newest first. */
  trail: { x: number; y: number }[];
  scene: Scene;
}

type Fill = string | CanvasGradient;

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function goldFill(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): Fill {
  const g = ctx.createLinearGradient(x, y, x + w, y + h);
  g.addColorStop(0, GOLD.light);
  g.addColorStop(0.45, GOLD.mid);
  g.addColorStop(1, GOLD.dark);
  return g;
}

/**
 * One plate seen from above, `len` studs long and one deep, at (col, row) in studs: dark edge,
 * light top-left and dark bottom-right inner lines, round studs with a shadow and a shine.
 */
export function drawPlate(
  ctx: CanvasRenderingContext2D,
  col: number,
  row: number,
  len: number,
  color: string,
  cell: number,
  opts: { gold?: boolean; cracked?: boolean } = {},
) {
  const g = Math.max(1, cell * 0.04);
  const x = col * cell + g, y = row * cell + g, w = len * cell - 2 * g, h = cell - 2 * g;
  const fill: Fill = opts.gold ? goldFill(ctx, x, y, w, h) : color;
  const line = Math.max(1, cell / 18);

  roundRect(ctx, x, y, w, h, Math.min(3, cell * 0.1));
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = "rgba(0,0,0,0.28)";
  ctx.stroke();

  ctx.lineWidth = line;
  ctx.beginPath();
  ctx.moveTo(x + line, y + h - line);
  ctx.lineTo(x + line, y + line);
  ctx.lineTo(x + w - line, y + line);
  ctx.strokeStyle = "rgba(255,255,255,0.28)";
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x + line, y + h - line * 0.75);
  ctx.lineTo(x + w - line * 0.75, y + h - line * 0.75);
  ctx.lineTo(x + w - line * 0.75, y + line);
  ctx.strokeStyle = "rgba(0,0,0,0.2)";
  ctx.stroke();

  // Studs: centred, one per whole stud.
  const count = Math.max(1, Math.floor(len + 1e-6));
  const pad = (len - count) / 2;
  const r = cell * 0.27;
  for (let i = 0; i < count; i++) {
    const cx = (col + pad + i + 0.5) * cell, cy = (row + 0.5) * cell - 1;
    ctx.beginPath();
    ctx.arc(cx, cy + 2, r, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(0,0,0,0.28)";
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx, cy + r * 0.25, r * 0.72, Math.PI * 1.15, Math.PI * 1.85);
    ctx.lineWidth = Math.max(1, cell / 22);
    ctx.strokeStyle = opts.gold ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.42)";
    ctx.stroke();
  }

  if (opts.cracked) {
    ctx.beginPath();
    [
      [0.58, 0],
      [0.47, 0.36],
      [0.63, 0.56],
      [0.52, 1],
    ].forEach(([px, py], i) => (i ? ctx.lineTo : ctx.moveTo).call(ctx, x + px * w, y + py * h));
    ctx.lineWidth = Math.max(1.2, cell / 20);
    ctx.lineJoin = "round";
    ctx.strokeStyle = CRACK;
    ctx.stroke();
  }
}

/** Baseplate and all bricks — the part that only changes when a brick does. */
export function drawWall(ctx: CanvasRenderingContext2D, bricks: Brick[], cell: number) {
  const w = COLS * cell, h = ROWS * cell;
  ctx.fillStyle = FIELD_BG;
  ctx.fillRect(0, 0, w, h);
  ctx.beginPath();
  for (let i = 1; i < COLS; i++) {
    ctx.moveTo(i * cell, 0);
    ctx.lineTo(i * cell, h);
  }
  for (let j = 1; j < ROWS; j++) {
    ctx.moveTo(0, j * cell);
    ctx.lineTo(w, j * cell);
  }
  ctx.lineWidth = 1;
  ctx.strokeStyle = "rgba(29,27,24,0.07)";
  ctx.stroke();
  for (const b of bricks) {
    drawPlate(ctx, b.col, b.row, b.len, b.color, cell, {
      gold: b.kind === "gold",
      cracked: b.kind === "black" && b.hits === 1,
    });
  }
}

/** A four-point star near a golden brick's top-left corner, pulsing unless motion is reduced. */
function drawGlint(ctx: CanvasRenderingContext2D, b: Brick, cell: number, t: number, reduced: boolean) {
  const phase = reduced ? 1 : 0.5 + 0.5 * Math.sin(((t + b.id * 0.37) / GLINT_PERIOD) * Math.PI * 2);
  const s = 0.75 + 0.45 * phase;
  const gx = (b.col + 0.2) * cell, gy = (b.row + 0.22) * cell;
  const a = cell * 0.2 * s, n = cell * 0.05 * s;
  ctx.beginPath();
  ctx.moveTo(gx, gy - a);
  ctx.lineTo(gx + n, gy - n);
  ctx.lineTo(gx + a, gy);
  ctx.lineTo(gx + n, gy + n);
  ctx.lineTo(gx, gy + a);
  ctx.lineTo(gx - n, gy + n);
  ctx.lineTo(gx - a, gy);
  ctx.lineTo(gx - n, gy - n);
  ctx.closePath();
  ctx.globalAlpha = 0.8 + 0.2 * phase; // always visible: the glint is how gold is told from yellow
  ctx.fillStyle = "#fffdf8";
  ctx.fill();
  ctx.globalAlpha = 1;
}

function drawBall(ctx: CanvasRenderingContext2D, x: number, y: number, cell: number) {
  const r = BALL_R * 1.1 * cell;
  ctx.beginPath();
  ctx.arc(x * cell, y * cell, r, 0, Math.PI * 2);
  ctx.fillStyle = "#fffdf8";
  ctx.fill();
  ctx.lineWidth = Math.max(1.5, cell * 0.07);
  ctx.strokeStyle = INK;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x * cell, y * cell, r * 0.5, 0, Math.PI * 2);
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = "rgba(29,27,24,0.35)";
  ctx.stroke();
}

/** Dotted line from the resting ball along the launch direction, with an arrowhead. */
function drawAim(ctx: CanvasRenderingContext2D, x: number, y: number, aim: number, cell: number) {
  const dx = Math.sin(aim), dy = -Math.cos(aim);
  const x0 = (x + dx * 0.55) * cell, y0 = (y + dy * 0.55) * cell;
  const x1 = (x + dx * 2.7) * cell, y1 = (y + dy * 2.7) * cell;
  ctx.save();
  ctx.globalAlpha = 0.5;
  ctx.strokeStyle = INK;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.lineWidth = Math.max(2, cell * 0.08);
  ctx.setLineDash([cell * 0.04, cell * 0.22]);
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.lineTo(x1, y1);
  ctx.stroke();
  ctx.setLineDash([]);
  const a = Math.atan2(y1 - y0, x1 - x0), L = cell * 0.36;
  ctx.beginPath();
  ctx.moveTo(x1 - L * Math.cos(a - 0.5), y1 - L * Math.sin(a - 0.5));
  ctx.lineTo(x1, y1);
  ctx.lineTo(x1 - L * Math.cos(a + 0.5), y1 - L * Math.sin(a + 0.5));
  ctx.stroke();
  ctx.restore();
}

function drawScene(ctx: CanvasRenderingContext2D, scene: Scene, cell: number) {
  for (const c of scene.chips) {
    ctx.save();
    ctx.translate(c.x * cell, c.y * cell);
    ctx.rotate(c.rot);
    ctx.globalAlpha = 0.85;
    drawPlate(ctx, -0.5, -0.5, 1, c.color, cell * 0.7);
    ctx.restore();
  }
  for (const p of scene.pops) {
    const alpha = Math.max(0, 1 - p.age / POP_LIFE);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `${Math.round(cell * (p.gold ? 0.66 : 0.5))}px "Archivo Black", sans-serif`;
    if (p.gold) {
      ctx.lineWidth = Math.max(2, cell * 0.1);
      ctx.strokeStyle = "#fffdf8";
      ctx.strokeText(p.text, p.x * cell, p.y * cell);
      ctx.fillStyle = GOLD.dark;
    } else {
      ctx.fillStyle = INK;
    }
    ctx.fillText(p.text, p.x * cell, p.y * cell);
    ctx.restore();
  }
}

/**
 * Keeps the wall drawn on an offscreen canvas and redraws it only when the wall or the size
 * changes, so a frame costs one image copy plus the moving parts.
 */
export class WallLayer {
  private canvas: HTMLCanvasElement | null = null;
  private key = "";

  constructor(private make: () => HTMLCanvasElement = () => document.createElement("canvas")) {}

  paint(ctx: CanvasRenderingContext2D, bricks: Brick[], version: number, cell: number, dpr: number) {
    const w = COLS * cell, h = ROWS * cell;
    const key = `${version}|${cell}|${dpr}`;
    if (this.key !== key) {
      if (!this.canvas) this.canvas = this.make();
      this.canvas.width = Math.round(w * dpr);
      this.canvas.height = Math.round(h * dpr);
      const off = this.canvas.getContext("2d");
      if (!off) {
        drawWall(ctx, bricks, cell); // no offscreen canvas: draw straight in
        return;
      }
      off.setTransform(dpr, 0, 0, dpr, 0, 0);
      drawWall(off, bricks, cell);
      this.key = key;
    }
    ctx.drawImage(this.canvas as HTMLCanvasElement, 0, 0, w, h);
  }
}

/** Draws one frame; the context is already scaled for the screen. `t` drives the glints. */
export function drawField(
  ctx: CanvasRenderingContext2D,
  frame: FieldFrame,
  cell: number,
  t: number,
  layer: WallLayer,
  dpr = 1,
  reduced = false,
) {
  layer.paint(ctx, frame.bricks, frame.wallVersion, cell, dpr);
  for (const b of frame.bricks) if (b.kind === "gold") drawGlint(ctx, b, cell, t, reduced);

  drawPlate(ctx, frame.paddle - PADDLE_W / 2, PADDLE_ROW, PADDLE_W, PADDLE_COLOR, cell);

  if (frame.serving) {
    drawAim(ctx, frame.ball.x, frame.ball.y, frame.aim, cell);
  } else {
    frame.trail.slice(0, 3).forEach((p, i) => {
      ctx.globalAlpha = [0.16, 0.1, 0.05][i];
      ctx.beginPath();
      ctx.arc(p.x * cell, p.y * cell, BALL_R * cell * (0.95 - i * 0.15), 0, Math.PI * 2);
      ctx.fillStyle = INK;
      ctx.fill();
    });
    ctx.globalAlpha = 1;
  }
  drawBall(ctx, frame.ball.x, frame.ball.y, cell);
  drawScene(ctx, frame.scene, cell);
}
